import type { LabTestTemplateSection } from '@type/labTestTep.type'
import { ReportLetterhead, ReportSignatureBlock, useFacilityReportBranding } from '@ui'
import { TemplateTableCellHtml } from '@ui/TemplateTableCellEditor'
import parse from 'html-react-parser'
import { labFlagBadge } from '../../../utils/labFlags'
import { LabFlagBadge } from '../LabFlagBadge'
import { LAB_RESULT_LOGO_URL, LAB_RESULT_STAMP_URL } from '../labTemplateFooter'
import type { DocTemplatePreviewDocument, DocTemplatePreviewTableSection } from './templateDefinition'
import { columnHeaderToRowKey } from './templateDefinition'

function isPreviewTableSection(section: LabTestTemplateSection): section is DocTemplatePreviewTableSection {
  return section.presentation === 'table'
}

function getTableCellValue(
  section: DocTemplatePreviewTableSection,
  rowIndex: number,
  colIndex: number,
  colHeader: string
): string {
  const previewRow = section.preview_row_cells?.[rowIndex]
  if (previewRow?.cells[colIndex] !== undefined) return previewRow.cells[colIndex]

  const row = [...section.rows].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[rowIndex]
  if (!row) return ''
  const key = columnHeaderToRowKey(colHeader, colIndex)
  const raw = String((row as unknown as Record<string, unknown>)[key] ?? '')
  if (raw.trim()) return raw
  if (section.editable_columns.includes(key) && key === 'result') return 'result'
  return ''
}

export type DocTemplatePreviewVariant = 'template' | 'snapshot'

type DocTemplatePreviewViewProps = {
  document: DocTemplatePreviewDocument
  /** `template` = builder/list preview; `snapshot` = saved lab result (no title badge). */
  variant?: DocTemplatePreviewVariant
}

const DocTemplatePreviewView = ({ document: doc, variant = 'template' }: DocTemplatePreviewViewProps) => {
  const isSnapshot = variant === 'snapshot'
  const stampSrc = LAB_RESULT_STAMP_URL
  const layouts = useFacilityReportBranding(doc.branding)

  return (
    <article className="mx-auto w-full max-w-[920px] rounded-xl border border-[#E8E8E8] bg-white px-6 py-8 text-[#111] shadow-sm sm:px-10 sm:py-10 print:max-w-none print:rounded-none print:border-0 print:px-2 print:py-2 print:shadow-none">
      <ReportLetterhead
        layout={layouts.letterhead_layout}
        letterheadHtml={doc.letterhead}
        logoAlt="Laboratory logo"
        logoUrl={LAB_RESULT_LOGO_URL}
      />
      {/* <div className="mb-4 flex flex-wrap items-center justify-center gap-3 print:mb-2">
        <h1 className="font-semibold uppercase tracking-[0.04em] text-[#111] sm:text-xl">{doc.name}</h1>
        {doc.category ? (
          <Badge
            label={doc.category}
            status="default"
            style={{
              background: '#2E4A2E',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 600,
              minWidth: 'auto',
              padding: '4px 12px',
              textTransform: 'capitalize'
            }}
          />
        ) : null}
      </div> */}

      {doc.header_fields?.length ? (
        <section className="border-b border-[#E6E6E6] py-5 print:py-2">
          <div className="grid gap-4 grid-cols-1 gap-y-4 sm:grid-cols-2 lg:grid-cols-4 print:gap-2 print:gap-y-1">
            {doc.header_fields.map(hf => (
              <div className="flex items-center gap-3 text-sm" key={hf.id}>
                <span className="text-[#555]">{hf.label}:</span>
                <span className="font-semibold text-[#111]">{hf.value?.trim() ? hf.value : 'NIL'}</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <div className="py-6 print:py-2">
        {doc?.sections?.map(section => {
          if (isPreviewTableSection(section)) {
            const sortedRows = [...section.rows].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            return (
              <section className="mb-8 print:mb-3" key={section.id}>
                <h2 className="mb-4 text-[15px] font-semibold text-[#111] print:mb-2">{section.title}</h2>
                <div className="overflow-hidden rounded-xl border border-[#D9D9D9] print:rounded-none">
                  <table className="w-full min-w-[520px] border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-[#D9D9D9] bg-[#FAFAFA]">
                        {section.table_columns.map(col => (
                          <th
                            className="px-4 py-3 text-left text-[13px] font-semibold capitalize text-[#444] print:px-2 print:py-1.5"
                            key={col}
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((row, rowIndex) => (
                        <tr className="border-b border-[#EEEEEE] last:border-b-0" key={row.id}>
                          {section.table_columns.map((col, colIndex) => {
                            // Flag column of a saved result: badge from the backend-computed `rows[].flag`.
                            if (columnHeaderToRowKey(col, colIndex) === 'flag' && labFlagBadge(row.flag)) {
                              return (
                                <td className="px-4 py-3 align-top print:px-2 print:py-1.5" key={`${row.id}-${col}`}>
                                  <LabFlagBadge flag={row.flag} />
                                </td>
                              )
                            }
                            const value = getTableCellValue(section, rowIndex, colIndex, col)
                            return (
                              <td
                                className="px-4 py-3 align-top text-[#333] print:px-2 print:py-1.5"
                                key={`${row.id}-${col}`}
                              >
                                <TemplateTableCellHtml html={value} />
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )
          }

          if (section.presentation === 'notes') {
            const { notes } = section
            const bodyText =
              notes.kind === 'static_text'
                ? (notes.default_text ?? '')
                : (notes.value ?? notes.placeholder ?? notes.label ?? '')
            return (
              <section className="mb-8 print:mb-3" key={section.id}>
                <h2 className="mb-3 text-[15px] font-semibold text-[#111] print:mb-1">{section.title}</h2>
                <div className="rounded-xl border border-[#D9D9D9] bg-[#FAFAFA] px-5 py-4 text-sm leading-relaxed text-[#333] print:rounded-none print:px-2 print:py-2">
                  {notes.kind === 'entry' && notes.label ? (
                    <p className="mb-2 text-xs font-medium text-[#666]">{notes.label}</p>
                  ) : null}
                  {bodyText ? (
                    <div className="prose prose-sm max-w-none">{parse(bodyText)}</div>
                  ) : (
                    <span className="text-[#999]">—</span>
                  )}
                </div>
              </section>
            )
          }

          if (section.presentation === 'key_value_list') {
            const sortedRows = [...section.rows].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            return (
              <section className="mb-8 print:mb-3" key={section.id}>
                <h2 className="mb-3 text-[15px] font-semibold text-[#111] print:mb-1">{section.title}</h2>
                <div className="space-y-2 rounded-xl border border-[#D9D9D9] p-4 print:rounded-none print:p-2 print:space-y-1">
                  {sortedRows.map(row => (
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-4" key={row.id}>
                      <span className="min-w-[10rem] text-sm font-medium text-[#555]">
                        {row.label ?? row.parameter}
                      </span>
                      <span className="flex-1 text-sm text-[#111]">{row.value ?? '—'}</span>
                    </div>
                  ))}
                </div>
              </section>
            )
          }

          return null
        })}
      </div>

      <footer className="border-t border-[#E6E6E6] pt-6 text-sm text-[#555] print:pt-2">
        {doc.footer?.correlation ? <p className="mb-8 leading-relaxed print:mb-3">{doc.footer.correlation}</p> : null}
        <ReportSignatureBlock
          layout={layouts.signature_layout}
          signatureSlot={
            <div
              className={
                layouts.signature_layout === 'right'
                  ? 'ml-auto w-[200px] max-w-full text-left'
                  : layouts.signature_layout === 'center'
                    ? 'mx-auto w-[200px] max-w-full text-left'
                    : 'w-[200px] max-w-full text-left'
              }
            >
              <p className="font-medium text-[#111]">
                {doc.footer?.signoff_role}
                {isSnapshot ? '' : ' Signature:*'}
              </p>
              {doc.footer?.Signature ? (
                <div className="mt-2 w-full [&_img]:block [&_img]:h-auto [&_img]:w-full [&_img]:max-w-full print:mt-1">
                  {parse(doc.footer.Signature)}
                </div>
              ) : (
                <div aria-hidden className="mt-10 w-full border-b border-[#333] print:mt-6" />
              )}
            </div>
          }
          stampUrl={stampSrc}
        />
      </footer>
    </article>
  )
}

export default DocTemplatePreviewView
